import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthenticationService } from '../shared/services/authentication.service';
import { Capacitor } from '@capacitor/core';
import { AuthService } from './auth.service';
import { CachingService } from '../shared/services/caching.service';
import { WtStorageService } from '../shared/services/wt-storage.service';

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {
  private enableDebugLogs = false; // Set to true to enable detailed debug logs
  constructor(
    private authService: AuthService,
    private authenticationService: AuthenticationService,
    private readonly wtStorage: WtStorageService,
    private cachingService: CachingService
  ) {}

  externalAPiList = ['api.what3words.com', `thingproxy.freeboard.io`, 'www.googleapis.com'];

  // External access endpoints that should use their own token (not OAuth)
  externalAccessEndpoints = ['ExternalAccess', 'ExternalPolicies', 'ExternalAccountSettings', 'ExternalJob', 'ExternalIncident', 'ExternalChecklistItem', 'ExternalDocuments', 'ExternalDocumentCategories'];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip external APIs
    if (this.externalAPiList.some((url) => req.url.includes(url))) {
      return next.handle(req);
    }

    // Extract and store Account UID from URL if present
    this.extractAndStoreAccountUID();

    // Check if MSAL app is initialized
    if (!this.authService.isAppInitialized()) {
      console.warn('MSAL not initialized. Skipping token injection.');

      // Even if MSAL is not initialized, we should still add the aid and AUID headers from localStorage
      // This is critical for page refresh scenarios where the first API call happens before MSAL is ready
      const storedAccountId = localStorage.getItem('selectedAccountId');
      const accountUID = this.getAccountUID();

      if (storedAccountId || accountUID) {
        const headers: { [key: string]: string } = {};

        if (storedAccountId) {
          headers['aId'] = storedAccountId;
        }

        if (accountUID) {
          headers['AUID'] = accountUID;
        }

        if (Capacitor.getPlatform() !== 'web') {
          headers['m'] = 'm';
        }

        const clonedRequest = req.clone({ setHeaders: headers });
        return next.handle(clonedRequest);
      }

      return next.handle(req); // Pass the request without modification if no stored account ID or UID
    }

    // Check if the user is authenticated
    if (this.authService.isAuthenticated()) {
      const timestamp = new Date().toISOString();
      const platform = Capacitor.getPlatform();

      // Convert the Promise from getBearerToken into an Observable
      return from(this.authService.getBearerToken()).pipe(
        switchMap((accessToken) => {
          if(this.enableDebugLogs)
          console.log(`[${timestamp}] [${platform.toUpperCase()}] Token acquired for request: ${req.method} ${req.url}`);

          const headers: { [key: string]: string } = {
            Authorization: `Bearer ${accessToken}`,
          };

          if (platform !== 'web') {
            headers['m'] = 'm';
            if(this.enableDebugLogs)
            console.log(`[${timestamp}] [${platform.toUpperCase()}] Added mobile platform header`);
          }

          // Try to get account ID from cache first, then fall back to localStorage
          let account = null;
          try {
            account = this.authenticationService?.getCurrentAccount();
          } catch (error) {
            console.warn(`[${timestamp}] [${platform.toUpperCase()}] Could not get current account:`, error);
          }

          if (account) {
            headers['aId'] = `${account.id}`;
          } else {
            // Fallback to localStorage for page refresh scenarios
            const storedAccountId = localStorage.getItem('selectedAccountId');
            if (storedAccountId) {
              headers['aId'] = storedAccountId;
            }
          }

          // Add Account UID header (with null safety)
          const accountUID = this.getAccountUID();
          if (accountUID) {
            headers['AUID'] = accountUID;
          }
          if(this.enableDebugLogs)
          console.log(`[${timestamp}] [${platform.toUpperCase()}] Request headers:`, {
            hasAuthorization: !!headers['Authorization'],
            hasAccountId: !!headers['aId'],
            hasAccountUID: !!headers['AUID'],
            isMobile: !!headers['m']
          });

          // Clone the request with the new headers
          const clonedRequest = req.clone({ setHeaders: headers });
          return next.handle(clonedRequest);
        }),
        catchError((error) => {
          // Handle token acquisition errors
          console.error(`[${timestamp}] [${platform.toUpperCase()}] ❌ Error acquiring token for request:`, error);
          console.error(`[${timestamp}] [${platform.toUpperCase()}] Request details:`, {
            method: req.method,
            url: req.url,
            errorMessage: error instanceof Error ? error.message : JSON.stringify(error)
          });

          // For mobile platform, provide more specific error message
          if (platform !== 'web') {
            console.error(`[${timestamp}] [${platform.toUpperCase()}] Mobile token acquisition failed. This may indicate:`);
            console.error(`  - Token has expired and refresh failed`);
            console.error(`  - No refresh token available`);
            console.error(`  - Network connectivity issues`);
            console.error(`  - OAuth2 configuration issues`);
            console.error(`[${timestamp}] [${platform.toUpperCase()}] Automatic re-authentication will be triggered by MobileOAuth2Service`);
          }

          // Re-throw the error to be handled by other interceptors
          // Note: For mobile, MobileOAuth2Service.getAccessToken() will have already triggered
          // automatic re-authentication if token refresh failed
          return throwError(() => error);
        })
      );
    }

    // If not authenticated, pass the request through without modification
    return next.handle(req);
  }

  /**
   * Extracts the Account UID (GUID) from the current URL path and stores it in CachingService.
   * Follows the same pattern as AID (Account ID) extraction and storage.
   * The Account UID is expected to be the first segment after the base URL.
   * Example: From '/600fdef8-e450-4ec8-9685-46d35911a49b/mytrack/planning', extracts '600fdef8-e450-4ec8-9685-46d35911a49b'
   */
  private extractAndStoreAccountUID(): void {
    // Check if cachingService is available
    if (!this.cachingService) {
      console.warn('[TokenInterceptor] CachingService not available, cannot extract Account UID');
      return;
    }

    try {
      const pathname = window.location.pathname;
      // Split the pathname and filter out empty strings
      const pathSegments = pathname.split('/').filter(segment => segment.length > 0);

      if (pathSegments.length > 0) {
        const potentialAccountUID = pathSegments[0];

        // Validate that it looks like a GUID (basic validation)
        // GUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (guidRegex.test(potentialAccountUID)) {
          // Store the Account UID using CachingService (same pattern as AID)
          this.cachingService.addAccountUID(potentialAccountUID);
        }
      }
    } catch (error) {
      // Silently fail if there's any error extracting the Account UID
      console.warn('Failed to extract Account UID from URL:', error);
    }
  }

  /**
   * Retrieves the Account UID from CachingService.
   * Follows the same pattern as AID (Account ID) retrieval.
   * @returns The Account UID or null if not found
   */
  private getAccountUID(): string | null {
    // Check if cachingService is available (may be null during early initialization)
    if (!this.cachingService) {
      console.warn('[TokenInterceptor] CachingService not available yet');
      return null;
    }

    try {
      // Get from CachingService (same pattern as AID)
      return this.cachingService.getAccountUID();
    } catch (error) {
      console.error('[TokenInterceptor] Error getting Account UID:', error);
      return null;
    }
  }
}
